import { create } from 'zustand';
import { Vector3 } from 'three';
import { saveJokesToStorage, getJokesFromStorage } from '../utils/persistenceUtils';

export type TowerType = 'science' | 'math' | 'art' | 'drama' | 'portal';
export type LevelType = 'default' | 'evanston';
export type DifficultyLevel = 'easy' | 'normal' | 'hard' | 'insane';

export interface Tower {
  id: string;
  type: TowerType;
  position: Vector3;
  level: number;
  range: number;
  damage: number;
  fireRate: number;
  freezeEffect?: number; // Slow effect percentage (0-1) for drama towers
  jokes?: string[]; // Array to store jokes for art towers
}

export interface Enemy {
  id: string;
  type: string;
  position: Vector3;
  health: number;
  maxHealth: number;
  speed: number;
  pathIndex: number;
  value: number;
  isFrozen: boolean;
  freezeMultiplier: number; // Value between 0-1 that slows down the enemy
  freezeDuration: number; // How long the freeze effect lasts
}

export interface GameState {
  // Game state
  isPlaying: boolean;
  isPaused: boolean;
  wave: number;
  score: number;
  resources: number;
  lives: number;
  
  // Entities
  towers: Tower[];
  enemies: Enemy[];
  selectedTower: Tower | null;
  selectedTowerType: TowerType | null;
  towerPreviewPosition: Vector3 | null;
  
  // Level selection
  currentLevel: LevelType;
  difficulty: DifficultyLevel;
  
  // Path for enemies to follow
  path: Vector3[];
  
  // Actions
  startGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  resetGame: () => void;
  addTower: (type: TowerType, position: Vector3) => void;
  upgradeTower: (id: string, customUpgrade?: Tower | null) => void;
  sellTower: (id: string) => void;
  selectTower: (id: string | null) => void;
  selectTowerType: (type: TowerType | null) => void;
  setTowerPreviewPosition: (position: Vector3 | null) => void;
  spawnEnemy: (type: string) => void;
  removeEnemy: (id: string) => void;
  damageEnemy: (id: string, damage: number, tower?: Tower | null) => void;
  updateEnemyPosition: (id: string, position: Vector3, pathIndex: number) => void;
  addResources: (amount: number) => void;
  loseLife: () => void;
  nextWave: () => void;
  changeLevel: (level: LevelType) => void;
  changeDifficulty: (difficulty: DifficultyLevel) => void;
  addTestJokeToDramaTowers: () => void;
  loadJokesForTower: (towerId: string) => void;
}

// Create a simple school layout path
const createDefaultSchoolPath = (): Vector3[] => {
  return [
    new Vector3(-20, 0, 0),      // Start outside the school
    new Vector3(-10, 0, 0),      // Approach the entrance
    new Vector3(-10, 0, 10),     // Turn towards the entrance
    new Vector3(0, 0, 10),       // Enter the main hallway
    new Vector3(0, 0, -5),       // Go down the main hallway
    new Vector3(10, 0, -5),      // Turn at the intersection
    new Vector3(10, 0, 5),       // Go towards the cafeteria
    new Vector3(15, 0, 5),       // Enter the cafeteria
    new Vector3(15, 0, -10),     // Go through the cafeteria
    new Vector3(5, 0, -10),      // Exit the cafeteria
    new Vector3(5, 0, -20),      // Head towards the gym
    new Vector3(-5, 0, -20),     // Enter the gym area
    new Vector3(-5, 0, -15),     // Move through the gym
    new Vector3(-15, 0, -15),    // Exit the gym
    new Vector3(-15, 0, -5),     // Head towards the library
    new Vector3(-5, 0, -5),      // Enter the library
    new Vector3(-5, 0, 0),       // Move through the library
    new Vector3(0, 0, 0),        // Reach the center of the school (target)
  ];
};

// Create Evanston Township High School layout path based on the floor plan
const createEvanstonSchoolPath = (): Vector3[] => {
  return [
    new Vector3(-20, 0, 20),     // Start at entrance #1 (bottom of map)
    new Vector3(-15, 0, 20),     // Enter the school
    new Vector3(-15, 0, 15),     // Move through East Cafeteria (E120)
    new Vector3(-10, 0, 15),     // Continue through East Cafeteria
    new Vector3(-10, 0, 10),     // Move to the main hallway
    new Vector3(-5, 0, 10),      // Continue through main hallway
    new Vector3(-5, 0, 5),       // Move towards the center
    new Vector3(0, 0, 5),        // Reach the center area (H)
    new Vector3(0, 0, 0),        // Move towards the north
    new Vector3(0, 0, -5),       // Continue north
    new Vector3(5, 0, -5),       // Turn towards West Cafeteria (W120)
    new Vector3(5, 0, 0),        // Enter West Cafeteria
    new Vector3(10, 0, 0),       // Move through West Cafeteria
    new Vector3(10, 0, -5),      // Exit West Cafeteria
    new Vector3(15, 0, -5),      // Move towards north entrance
    new Vector3(15, 0, -10),     // Continue towards north entrance
    new Vector3(10, 0, -10),     // Turn towards South Cafeteria (S120)
    new Vector3(10, 0, -15),     // Enter South Cafeteria
    new Vector3(5, 0, -15),      // Move through South Cafeteria
    new Vector3(0, 0, -15),      // Exit South Cafeteria
    new Vector3(0, 0, -20),      // Reach the Gym (G) - final target
  ];
};

// Get path based on level
const getPathForLevel = (level: LevelType): Vector3[] => {
  switch (level) {
    case 'evanston':
      return createEvanstonSchoolPath();
    case 'default':
    default:
      return createDefaultSchoolPath();
  }
};

// Tower stats
const towerStats: Record<TowerType, { range: number, damage: number, fireRate: number, cost: number, freezeEffect?: number }> = {
  science: { range: 8, damage: 13, fireRate: 1, cost: 100 },
  math: { range: 6, damage: 25, fireRate: 0.5, cost: 150 },
  art: { range: 10, damage: 10, fireRate: 1.5, cost: 125 },
  drama: { range: 12, damage: 8, fireRate: 1, cost: 175, freezeEffect: 0.5 }, // Drama tower slows enemies by 50%
  portal: { range: 0, damage: 0, fireRate: 0, cost: 0 }, // Portal tower is free and has no combat stats
};

// Enemy types
const enemyTypes: Record<string, { health: number, speed: number, value: number }> = {
  alien: { health: 100, speed: 0.1, value: 25 },
  ufo: { health: 200, speed: 0.06, value: 50 },
  boss: { health: 500, speed: 0.04, value: 150 },
};

// Difficulty modifiers
const difficultyModifiers: Record<DifficultyLevel, { towerMultiplier: number, enemyMultiplier: number }> = {
  easy: { towerMultiplier: 1.2, enemyMultiplier: 0.8 },    // Towers 20% stronger, enemies 20% weaker
  normal: { towerMultiplier: 1.0, enemyMultiplier: 1.0 },  // Standard balance
  hard: { towerMultiplier: 0.9, enemyMultiplier: 1.1 },    // Towers 10% weaker, enemies 10% stronger
  insane: { towerMultiplier: 0.6, enemyMultiplier: 1.4 },  // Towers 40% weaker, enemies 40% stronger
};

export const useGameStore = create<GameState>((set, get) => ({
  // Initial state
  isPlaying: false,
  isPaused: false,
  wave: 0,
  score: 0,
  resources: 500, // This will be the default, but will be updated based on difficulty when game starts
  lives: 10,
  
  towers: [],
  enemies: [],
  selectedTower: null,
  selectedTowerType: null,
  towerPreviewPosition: null,
  
  currentLevel: 'default',
  difficulty: 'normal',
  path: getPathForLevel('default'),
  
  // Game actions
  startGame: () => {
    const { difficulty } = get();
    set({ 
      isPlaying: true, 
      wave: 1,
      resources: difficulty === 'insane' ? 400 : 500
    });
  },
  
  pauseGame: () => set({ isPaused: true }),
  
  resumeGame: () => set({ isPaused: false }),
  
  resetGame: () => {
    const { difficulty } = get();
    set({
      isPlaying: false,
      isPaused: false,
      wave: 0,
      score: 0,
      resources: difficulty === 'insane' ? 400 : 500,
      lives: 10,
      towers: [],
      enemies: [],
      selectedTower: null,
      selectedTowerType: null,
      currentLevel: get().currentLevel,
      difficulty: difficulty, // Maintain current difficulty
      path: get().path,
    });
  },
  
  addTower: (type, position) => {
    const { resources, towers, difficulty } = get();
    const cost = towerStats[type].cost;
    
    console.log('Attempting to add tower:', {
      type,
      position,
      resources,
      cost,
      difficulty,
      currentTowers: towers.length
    });
    
    // Check if we have enough resources
    if (resources < cost) {
      console.log('Not enough resources');
      return;
    }
    
    // Check if position is already occupied
    const isOccupied = towers.some(tower => 
      tower.position.distanceTo(position) < 1
    );
    
    if (isOccupied) {
      console.log('Position is occupied');
      return;
    }
    
    // Apply difficulty modifier to tower stats
    const modifier = difficultyModifiers[difficulty].towerMultiplier;
    
    // Safely get the freezeEffect value
    const towerFreezeEffect = towerStats[type].freezeEffect;
    
    // Generate tower ID
    const towerId = `tower-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newTower: Tower = {
      id: towerId,
      type,
      position,
      level: 1,
      range: towerStats[type].range * modifier,
      damage: towerStats[type].damage * modifier,
      fireRate: towerStats[type].fireRate * modifier,
      freezeEffect: towerFreezeEffect !== undefined ? towerFreezeEffect * modifier : undefined,
      jokes: type === 'drama' ? [] : undefined  // Initialize empty jokes array for drama towers
    };
    
    console.log('Adding new tower:', newTower);
    
    set(state => {
      console.log('Updating game state with new tower');
      return {
        towers: [...state.towers, newTower],
        resources: state.resources - cost
      };
    });
  },
  
  upgradeTower: (id, customUpgrade = null) => {
    const { towers, resources, difficulty } = get();
    const tower = towers.find(t => t.id === id);
    
    if (!tower) {
      console.error(`Tower with id ${id} not found`);
      return;
    }
    
    // Don't charge resources when upgrading using a special ability like telling a joke
    const isSpecialUpgrade = customUpgrade !== null;
    const upgradeCost = isSpecialUpgrade ? 0 : towerStats[tower.type].cost * tower.level;
    
    if (resources < upgradeCost) {
      console.log(`Not enough resources to upgrade. Need ${upgradeCost}, have ${resources}`);
      return;
    }

    // Get difficulty modifier
    const modifier = difficultyModifiers[difficulty].towerMultiplier;
    
    console.log(`Upgrading tower ${id} with${customUpgrade ? ' custom' : ''} upgrade`);
    
    set(state => {
      // Update towers
      const updatedTowers = state.towers.map(t => {
        if (t.id === id) {
          // If a custom upgrade is provided, use it instead of the standard upgrade
          if (customUpgrade) {
            // Create the upgraded tower
            const upgradedTower = {
              ...customUpgrade,
              // Ensure the jokes array is preserved
              jokes: customUpgrade.jokes || t.jokes || []
            };

            // If the jokes array has changed, save it to localStorage
            if (upgradedTower.jokes !== t.jokes) {
              saveJokesToStorage(id, upgradedTower.jokes || []);
            }

            return upgradedTower;
          }
          
          // Standard upgrade - applying difficulty modifier to the upgrade bonuses
          const upgradedTower = { 
            ...t, 
            level: t.level + 1, 
            range: t.range * 1.4,
            damage: t.damage * 1.5,
            fireRate: t.fireRate * 1.3,
            // Preserve the jokes array
            jokes: t.jokes || []
          };
          
          // Improve freeze effect for drama towers
          if (t.type === 'drama') {
            const currentFreezeEffect = t.freezeEffect;
            if (currentFreezeEffect !== undefined) {
              upgradedTower.freezeEffect = Math.min(0.9, currentFreezeEffect + 0.1 * modifier); // Increase freeze effect by 10% up to max of 90%, adjusted by difficulty
            }
          }
          
          return upgradedTower;
        }
        return t;
      });

      return {
        towers: updatedTowers,
        resources: state.resources - (isSpecialUpgrade ? 0 : upgradeCost)
      };
    });
  },
  
  sellTower: (id) => {
    const { towers } = get();
    const tower = towers.find(t => t.id === id);
    
    if (!tower) return;
    
    const sellValue = Math.floor(towerStats[tower.type].cost * tower.level * 0.7);
    
    set(state => ({
      towers: state.towers.filter(t => t.id !== id),
      resources: state.resources + sellValue,
      selectedTower: null
    }));
  },
  
  selectTower: (id) => {
    if (id === null) {
      set({ selectedTower: null });
      return;
    }
    
    const { towers } = get();
    const tower = towers.find(t => t.id === id);
    
    if (tower) {
      set({ selectedTower: tower, selectedTowerType: null });
    }
  },
  
  selectTowerType: (type) => set({ 
    selectedTowerType: type,
    towerPreviewPosition: null // Reset preview when changing tower type
  }),
  
  setTowerPreviewPosition: (position) => set({ towerPreviewPosition: position }),
  
  spawnEnemy: (type) => {
    const { path, difficulty } = get();
    const enemyData = enemyTypes[type];
    
    if (!enemyData) return;
    
    // Apply difficulty modifier to enemy stats
    const modifier = difficultyModifiers[difficulty].enemyMultiplier;
    
    const newEnemy: Enemy = {
      id: `enemy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      position: path[0].clone(),
      health: enemyData.health * modifier,
      maxHealth: enemyData.health * modifier,
      speed: enemyData.speed * modifier,
      pathIndex: 0,
      value: enemyData.value,
      isFrozen: false,
      freezeMultiplier: 1,
      freezeDuration: 0
    };
    
    set(state => ({
      enemies: [...state.enemies, newEnemy]
    }));
  },
  
  removeEnemy: (id) => {
    // Check if the enemy exists before attempting to remove it
    const { enemies } = get();
    const enemyExists = enemies.some(e => e.id === id);
    
    if (enemyExists) {
      set(state => ({
        enemies: state.enemies.filter(e => e.id !== id)
      }));
    } else {
      console.warn(`Attempted to remove non-existent enemy with id: ${id}`);
    }
  },
  
  damageEnemy: (id, damage, tower = null) => {
    const { enemies, score, removeEnemy, addResources } = get();
    const enemy = enemies.find(e => e.id === id);
    
    if (!enemy) return;
    
    const newHealth = enemy.health - damage;
    
    if (newHealth <= 0) {
      // Enemy defeated
      removeEnemy(id);
      addResources(enemy.value);
      set(state => ({ score: state.score + enemy.value }));
      return;
    }
    
    // Apply freeze effect if the tower is a drama tower
    let freezeMultiplier = enemy.freezeMultiplier;
    let freezeDuration = enemy.freezeDuration;
    
    if (tower && tower.type === 'drama') {
      const towerFreezeEffect = tower.freezeEffect;
      if (towerFreezeEffect !== undefined) {
        // Apply freeze effect (3 seconds duration)
        // freezeEffect is a value between 0-1 that represents how much to slow the enemy
        // A freezeEffect of 0.5 means the enemy moves at 50% speed, so freezeMultiplier should be 0.5
        freezeMultiplier = 1 - towerFreezeEffect; // Convert slow percentage to speed multiplier
        freezeDuration = 3;
      }
    }
    
    set(state => ({
      enemies: state.enemies.map(e => 
        e.id === id ? { 
          ...e, 
          health: newHealth,
          isFrozen: freezeDuration > 0,
          freezeMultiplier: freezeDuration > 0 ? freezeMultiplier : 1,
          freezeDuration: freezeDuration
        } : e
      )
    }));
  },
  
  updateEnemyPosition: (id, position, pathIndex) => {
    const { enemies, path, loseLife, removeEnemy } = get();
    const enemy = enemies.find(e => e.id === id);
    
    if (!enemy) return;
    
    // Check if enemy reached the end
    if (pathIndex >= path.length - 1) {
      loseLife();
      removeEnemy(id);
      return;
    }
    
    // Update freeze duration
    let freezeDuration = enemy.freezeDuration;
    let isFrozen = enemy.isFrozen;
    let freezeMultiplier = enemy.freezeMultiplier;
    
    if (freezeDuration > 0) {
      freezeDuration -= 0.016; // Assuming 60 FPS, this is roughly 1 second per 60 frames
      
      if (freezeDuration <= 0) {
        freezeDuration = 0;
        isFrozen = false;
        freezeMultiplier = 1;
      }
    }
    
    // Check if the enemy still exists before updating
    const enemyStillExists = get().enemies.some(e => e.id === id);
    
    if (enemyStillExists) {
      set(state => ({
        enemies: state.enemies.map(e => 
          e.id === id ? { 
            ...e, 
            position, 
            pathIndex,
            freezeDuration,
            isFrozen,
            freezeMultiplier
          } : e
        )
      }));
    }
  },
  
  addResources: (amount) => {
    set(state => ({
      resources: state.resources + amount
    }));
  },
  
  loseLife: () => {
    const { lives, resetGame } = get();
    
    if (lives <= 1) {
      // Game over
      resetGame();
      return;
    }
    
    set(state => ({
      lives: state.lives - 1
    }));
  },
  
  nextWave: () => {
    set(state => ({
      wave: state.wave + 1
    }));
  },
  
  changeLevel: (level) => {
    set({ 
      currentLevel: level,
      path: getPathForLevel(level)
    });
  },
  
  changeDifficulty: (difficulty) => {
    set({ difficulty });
  },
  
  // Add a test joke to all drama towers (for testing)
  addTestJokeToDramaTowers: () => {
    const testJoke = 'Why did the scarecrow win an award? Because he was outstanding in his field!';
    
    set(state => {
      const updatedTowers = state.towers.map(tower => {
        if (tower.type === 'drama') {
          // Add a test joke if there are no jokes yet
          if (!tower.jokes || tower.jokes.length === 0) {
            const updatedJokes = [testJoke];
            
            // Save jokes to storage
            saveJokesToStorage(tower.id, updatedJokes);
            
            return {
              ...tower,
              jokes: updatedJokes
            };
          }
        }
        return tower;
      });
      
      return { towers: updatedTowers };
    });
  },
  
  // Load jokes for a drama tower from storage
  loadJokesForTower: (towerId) => {
    const { towers } = get();
    const tower = towers.find(t => t.id === towerId);
    
    if (!tower || tower.type !== 'drama') return;
    
    // Load jokes from storage
    const jokes = getJokesFromStorage(towerId);
    
    // Only update if we found jokes
    if (jokes.length > 0) {
      set(state => ({
        towers: state.towers.map(t => 
          t.id === towerId ? { ...t, jokes } : t
        )
      }));
    }
  }
})); 